import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Opponent } from '../../types';
import { Button } from '../../ui/button';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Text } from '../../ui/text';
import { EmptyState, Panel, StatCard } from '../../components/PageChrome';

interface OpponentFormState {
  name: string;
  notes: string;
}

function OpponentForm({
  initial,
  busy,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: OpponentFormState;
  busy: boolean;
  error?: string;
  submitLabel: string;
  onSubmit: (state: OpponentFormState) => Promise<void>;
  onCancel: () => void;
}) {
  const [state, setState] = useState(initial);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(state);
      }}
      className="space-y-4"
    >
      <Field>
        <Label>Opponent name</Label>
        <Input
          required
          value={state.name}
          onChange={(e) => setState({ ...state, name: e.target.value })}
          placeholder="e.g., Eastside Eagles"
        />
      </Field>
      <Field>
        <Label>Notes</Label>
        <Textarea
          rows={2}
          value={state.notes}
          onChange={(e) => setState({ ...state, notes: e.target.value })}
          placeholder="Optional scouting notes, colors, or scheduling context"
        />
      </Field>
      {error ? <ErrorMessage>{error}</ErrorMessage> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" color="dark/zinc" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function Opponents() {
  const { teamId } = useParams<{ teamId: string }>();

  const [opponents, setOpponents] = useState<Opponent[]>([]);
  const [error, setError] = useState<string>();
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState<string>();

  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!teamId) return;
    try {
      const res = await admin.listOpponents(teamId);
      setOpponents([...res.opponents].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError(String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const createOpponent = async (form: OpponentFormState) => {
    if (!teamId) return;
    setBusy(true);
    setError(undefined);
    try {
      await admin.createOpponent(teamId, {
        name: form.name.trim(),
        notes: form.notes.trim() || undefined,
      });
      setShowNew(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const updateOpponent = (opponentId: string) => async (form: OpponentFormState) => {
    if (!teamId) return;
    setBusy(true);
    setError(undefined);
    try {
      await admin.updateOpponent(teamId, opponentId, {
        name: form.name.trim(),
        notes: form.notes.trim() || null,
      });
      setEditingId(undefined);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (o: Opponent) => {
    if (!teamId) return;
    if (!window.confirm(`Delete ${o.name}?`)) return;
    try {
      await admin.deleteOpponent(teamId, o.opponentId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Opponents</Heading>
          <Text className="mt-1 text-sm">
            Save repeat opponents so schedules stay consistent and easier to scan.
          </Text>
        </div>
        <Button color="dark/zinc" onClick={() => {
          setShowNew((v) => !v);
          setEditingId(undefined);
        }}>
          {showNew ? 'Cancel' : 'Add opponent'}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard label="Saved opponents" value={opponents.length} />
        <StatCard label="With notes" value={opponents.filter((o) => o.notes).length} />
      </div>

      {showNew ? (
        <Panel>
          <OpponentForm
            initial={{ name: '', notes: '' }}
            busy={busy}
            error={error}
            submitLabel="Save opponent"
            onSubmit={createOpponent}
            onCancel={() => setShowNew(false)}
          />
        </Panel>
      ) : null}

      {error && !showNew ? <Text className="text-red-400">{error}</Text> : null}

      {opponents.length === 0 ? (
        <EmptyState
          title="No opponents yet"
          body="Add common opponents once, then pick them while scheduling games."
          action={<Button color="dark/zinc" onClick={() => setShowNew(true)}>Add opponent</Button>}
        />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {opponents.map((o) => (
            <li key={o.opponentId} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              {editingId === o.opponentId ? (
                <OpponentForm
                  initial={{ name: o.name, notes: o.notes ?? '' }}
                  busy={busy}
                  error={error}
                  submitLabel="Save changes"
                  onSubmit={updateOpponent(o.opponentId)}
                  onCancel={() => setEditingId(undefined)}
                />
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-zinc-100">{o.name}</div>
                      <Text className="mt-1 text-sm">
                        {o.notes || 'No notes yet.'}
                      </Text>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <Button plain onClick={() => setEditingId(o.opponentId)}>
                      Edit
                    </Button>
                    <Button plain onClick={() => void remove(o)} className="text-red-400">
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
